import React, { useCallback, useEffect, useState } from 'react';
import { FileSearch, Search, Calendar, User as UserIcon } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { capaInvestigationsService, CapaInvestigation, InvestigationStatus } from '../../services/capaInvestigationsService';
import InvestigationView from '../../views/capa/InvestigationView';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import { usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const ACT_INVESTIGATIONS = 'CAP_INVESTIGATIONS';

const InvestigationsTab: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_INVESTIGATIONS, 'view');
    const { formatDate } = useEntitySettings();
    const [openInvId, setOpenInvId] = useState<number | null>(null);
    const [rows, setRows] = useState<CapaInvestigation[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<InvestigationStatus>('OPEN');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const load = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const { data, total } = await capaInvestigationsService.listPaged({
                inv_status: statusFilter,
                q: search || undefined,
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setRows(data);
            setTotalRecords(total);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load investigations') });
        } finally { setLoading(false); }
    }, [statusFilter, search, page, pageSize, canView]);

    useEffect(() => { setPage(1); }, [statusFilter, search, pageSize]);
    useEffect(() => { load(); }, [load]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to CAPA investigations." />
            </div>
        );
    }

    return (
        <div className="flex h-full overflow-hidden">
            {/* Toolbar + table */}
            <div className="netbody-card flex flex-col flex-1 min-w-0">
                <div className="netbody-header">
                    <div className="flex items-center gap-2 ml-auto">
                        <FormControl.SearchSelect
                            value={statusFilter}
                            onChange={v => setStatusFilter((v ?? 'OPEN') as InvestigationStatus)}
                            className="filter-status"
                            data-testid="SEL_capa_inv_status_filter"
                            noSearchIcon
                            options={[
                                { value: 'OPEN', label: 'Open' },
                                { value: 'CLOSED', label: 'Closed' },
                            ]}
                        />
                        <FormControl.Input
                            type="text"
                            placeholder="Search investigations…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            leadingIcon={<Search size={14} />}
                            className="filter-search"
                            data-testid="INP_capa_inv_search"
                        />
                    </div>
                </div>

                <div className="netbody-content">
                    <div className="scroll-container flex-1 overflow-hidden">
                        <div className="table-list">
                            <div className="table-list-scroll">
                                <table className="table-list-table">
                                    <thead>
                                        <tr>
                                            <th className="table-list-th table-list-cell--sno">S.No</th>
                                            <th className="table-list-th">Issue</th>
                                            <th className="table-list-th">Owner</th>
                                            <th className="table-list-th">Team</th>
                                            <th className="table-list-th">Impacts</th>
                                            <th className="table-list-th">Causes</th>
                                            <th className="table-list-th">Actions</th>
                                            <th className="table-list-th">Due</th>
                                        </tr>
                                    </thead>
                                    <tbody aria-busy={loading} aria-live="polite">
                                        {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'sm', 'sm', 'sm', 'sm', 'sm']} />}
                                        {!loading && rows.length === 0 && (
                                            <tr>
                                                <td colSpan={8} className="table-list-cell">
                                                    <NoDataFound
                                                        title={search || statusFilter !== 'OPEN' ? 'No Matching Investigations' : 'No Investigations Found'}
                                                        message={search || statusFilter !== 'OPEN' ? 'No investigations match your search or filter.' : 'Send an issue to investigation to get started.'}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                        {!loading && rows.map((inv, idx) => (
                                            <tr
                                                key={inv.id}
                                                className="table-list-row"
                                                onClick={() => setOpenInvId(inv.id)}
                                                data-testid={`BTN_capa_inv_row_${inv.id}_open`}
                                            >
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">
                                                        {inv.code && <span className="text-muted text-xs mr-1">{inv.code}</span>}
                                                        {inv.issue_code}
                                                    </span>
                                                    <span className="table-list-cell-sub">{inv.issue_name}</span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="inline-flex items-center gap-1 text-xs">
                                                        <UserIcon size={11} /> {inv.owner_name || `user#${inv.owner_user_id}`}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">{inv.team_count ?? 0}</td>
                                                <td className="table-list-cell">{inv.impact_count ?? 0}</td>
                                                <td className="table-list-cell">{inv.rca_count ?? 0}</td>
                                                <td className="table-list-cell">{inv.task_count ?? 0}</td>
                                                <td className="table-list-cell">
                                                    {inv.due_date && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                                            <Calendar size={11} /> {formatDate(inv.due_date)}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                pageSize={pageSize}
                                totalRecords={totalRecords}
                                onPageChange={setPage}
                                onPageSizeChange={setPageSize}
                                pageSizeOptions={[10, 25, 50, 100]}
                                recordLabel="investigations"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {openInvId !== null && (
                <Modal isOpen={true} onClose={() => { setOpenInvId(null); load(); }} size="xl">
                    <InvestigationView
                        investigationIdProp={openInvId}
                        onClose={() => { setOpenInvId(null); load(); }}
                    />
                </Modal>
            )}

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default InvestigationsTab;
